/**
 * Phase N — /terms · Terms of Service (draft, pending legal review).
 *
 * TODO: confirm exact legal entity name.
 * TODO: pending attorney review before public launch.
 */
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import LegalReviewBanner from "@/components/LegalReviewBanner";

const LAST_UPDATED = "February 2026";

function LegalSection({ title, id, testId, children }) {
  return (
    <section id={id} data-testid={testId} className="mt-10">
      <h2 className="font-serif text-2xl text-ivy tracking-tight">{title}</h2>
      <div className="mt-4 text-ink-soft leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <Layout>
      <SEO
        title="Terms of Service | Beyond Valencia"
        description="Beyond Valencia terms of service — the agreement between you and Beyond Valencia when you use our platform, tools, and adviser services."
        canonical="/terms"
      />
      <LegalReviewBanner />

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="terms-page">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold">Legal · Draft</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ivy tracking-tight" data-testid="terms-h1">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-stone">Last updated: {LAST_UPDATED}</p>

        <p className="mt-8 text-ink-soft leading-relaxed">
          {/* TODO: confirm exact legal entity name */}
          These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of Beyond Valencia LLC&apos;s
          website, tools, and services (collectively, the &ldquo;Services&rdquo;). By using the Services you agree
          to these Terms. If you do not agree, do not use the Services.
        </p>

        <LegalSection title="1. Who can use the Services" id="eligibility" testId="terms-eligibility">
          <p>You must be at least 13 years old to use the Services. If you are under 18, you must have permission from a parent or legal guardian to purchase any paid Service. Certain Services are intended for prospective and current college transfer students.</p>
        </LegalSection>

        <LegalSection title="2. Accounts" id="accounts" testId="terms-accounts">
          <p>You are responsible for the information you provide, the accuracy of your transfer profile, and the confidentiality of your account. You must not share your credentials or misrepresent your identity or academic history.</p>
        </LegalSection>

        <LegalSection title="3. Nature of our services" id="nature" testId="terms-nature">
          <p>
            Beyond Valencia provides admissions strategy, diagnostic tools, and human advising for college transfer
            students. We do not guarantee admission to any university, financial aid outcome, or specific credit
            articulation. Admissions decisions are made by universities, not by us.
          </p>
          <p>
            The requirement graph, prerequisite lists, and articulation notes on the platform are compiled from
            public sources cited on each page. We make good-faith efforts to keep them accurate but universities may
            change their requirements without notice. You are responsible for confirming any material requirement
            with the destination institution before relying on it.
          </p>
        </LegalSection>

        <LegalSection title="4. Payments and pricing" id="payments" testId="terms-payments">
          <p>
            Paid Services are billed at the price displayed at the point of purchase. Prices are in U.S. dollars.
            Payments are processed by Stripe on our behalf. By purchasing a paid Service you authorize us and Stripe
            to charge the payment method you provide.
          </p>
          <p>Refunds are governed by our{" "}
            <a href="/refund-policy" className="text-ivy hover:text-brass underline" data-testid="terms-refund-link">Refund Policy</a>.
          </p>
        </LegalSection>

        <LegalSection title="5. Acceptable use" id="acceptable" testId="terms-acceptable">
          <ul className="list-disc pl-5 space-y-2">
            <li>Do not use the Services to break the law, violate any university&apos;s academic integrity policy, or misrepresent your academic record.</li>
            <li>Do not scrape, reverse-engineer, or otherwise attempt to extract our verified requirement graph in bulk.</li>
            <li>Do not upload malware, harass other users, or disrupt the Services.</li>
          </ul>
          <p>We may suspend or terminate your access for any material violation.</p>
        </LegalSection>

        <LegalSection title="6. Intellectual property" id="ip" testId="terms-ip">
          <p>
            The Services — including the requirement graph, code, copy, design, and any AI-generated summaries
            produced within the platform — are owned by Beyond Valencia or licensed to us. You may use the Services
            for your personal transfer planning. Any other use requires our written permission.
          </p>
          <p>You retain ownership of transcripts and other materials you upload. You grant us a limited license to process those materials solely to deliver the Services to you.</p>
        </LegalSection>

        <LegalSection title="7. Third-party services" id="third-parties" testId="terms-third-parties">
          <p>
            The Services rely on third-party providers, including Google (authentication), Stripe (payments),
            Emergent (authentication and storage), and Resend (email). Your use of those providers is governed by
            their respective terms. We may route your inquiry to our partner Ivy League Consulting when your target
            list makes that partner a better fit — you consent to that routing by using the intake.
          </p>
        </LegalSection>

        <LegalSection title="8. Disclaimers" id="disclaimers" testId="terms-disclaimers">
          <p>
            The Services are provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. To the fullest extent
            permitted by law, we disclaim all warranties, express or implied, including warranties of
            merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </LegalSection>

        <LegalSection title="9. Limitation of liability" id="liability" testId="terms-liability">
          <p>
            To the fullest extent permitted by law, Beyond Valencia will not be liable for indirect, incidental,
            special, consequential, or punitive damages, or for lost profits, revenue, data, or good will. Our
            total liability for any claim arising from or relating to the Services will not exceed the greater of
            (a) the amount you paid us in the twelve months before the event giving rise to the claim, or (b) one
            hundred U.S. dollars.
          </p>
        </LegalSection>

        <LegalSection title="10. Governing law" id="law" testId="terms-law">
          <p>
            {/* TODO: confirm governing law with counsel */}
            These Terms are governed by the laws of the State of Florida, without regard to its conflict of laws
            rules. Any dispute arising out of or relating to these Terms will be resolved in the state or federal
            courts located in Orange County, Florida.
          </p>
        </LegalSection>

        <LegalSection title="11. Changes to these Terms" id="changes" testId="terms-changes">
          <p>We may update these Terms from time to time. Continued use of the Services after we post updated Terms constitutes acceptance.</p>
        </LegalSection>

        <LegalSection title="12. Contact" id="contact" testId="terms-contact">
          <p>
            {/* TODO: confirm exact legal entity name and mailing address before publishing */}
            Beyond Valencia LLC · Orlando, Florida ·{" "}
            <a href="mailto:hello@beyondvalencia.com" className="text-ivy hover:text-brass underline">
              hello@beyondvalencia.com
            </a>
          </p>
        </LegalSection>

        <p className="mt-16 text-xs text-stone italic" data-testid="terms-draft-disclaimer">
          This document is a draft pending legal review. It is not a substitute for advice from qualified counsel.
        </p>
      </div>
    </Layout>
  );
}
